import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { FaStarOfLife } from "react-icons/fa";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

function FormInputField({
  name = "name",
  label,
  placeholder = "",
  disabled = false,
  type = "text",
  form = [],
  selectOption = [],
  radioOption = [],
  required = false,
  style = "",
  parseAs = "number",
  dateFormat = "",
  timeFormat = "HH:mm",
  search = false,
  clearable = false,
  textAreaValue = "",
  autoSetToday = true
}) {
  const [open, setOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [date, setDate] = useState(undefined);
  const [searchTerm, setSearchTerm] = useState("");
  const filteredOptions = selectOption?.filter((opt) =>
    opt?.label?.toLowerCase()?.includes(searchTerm?.toLocaleLowerCase())
  );

  useEffect(() => {
    if (
      type === "date" &&
      autoSetToday &&
      !form.getValues(name)
    ) {
      const today = new Date();
      form.setValue(name, today.toISOString());
      setDate(today);
    }
  }, [type, name, form, autoSetToday]);


  return (
    <FormField
      control={form?.control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-col gap-1 w-full relative">
          {label &&
            <FormLabel className="text-cwhite flex gap-1 items-center">
              {type === "checkbox" ? "" : label}
              {required && <FaStarOfLife className="text-red-500 text-[8px]" />}
            </FormLabel>
          }

          {/* Input Types */}
          <FormControl>
            <>
              {type === "text" && (
                <Input
                  disabled={disabled}
                  placeholder={placeholder}
                  className={style}
                  {...field}
                />
              )}

              {type === "password" && (
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder={placeholder}
                    className={style}
                    {...field}
                  />
                  <div
                    className="absolute right-3 top-2.5 cursor-pointer text-gray-400"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </div>
                </div>
              )}

              {type === "select" && (
                <Popover open={open} onOpenChange={setOpen}>
                  <PopoverTrigger asChild>
                    <button
                      disabled={disabled}
                      className={cn(
                        `${disabled && "opacity-50 cursor-not-allowed"} w-full text-left flex items-center font-normal px-3 min-h-10 border border-gray-600 rounded-md text-white`,
                        (!field.value || field.value === "false" || !filteredOptions?.some((opt) => String(opt.value) === String(field.value))) && "text-muted-foreground",
                        style
                      )}
                    >
                      {field.value !== undefined && field.value !== null && field.value !== "" && field.value !== "false" && field.value !== false
                        ? filteredOptions?.find(
                            (opt) => String(opt.value) === String(field.value)
                          )?.label || placeholder
                        : placeholder}
                    </button>
                  </PopoverTrigger>

                  <PopoverContent
                    className="min-w-[--radix-popover-trigger-width] bg-cblack7 p-2 max-h-[300px] overflow-y-auto border-gray-600"
                    align="start"
                  >
                    {/* Search bar */}
                    {search && (
                      <div className="mb-2">
                        <Input
                          placeholder="Search..."
                          className="bg-cblack6/40 text-cwhite border border-gray-600"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          onMouseDown={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}

                    {/* Clear option */}
                    {clearable && (
                      <div
                        onClick={() => field.onChange(null)}
                        className="cursor-pointer text-sm text-red-400 p-2 hover:bg-cblack6 rounded-sm"
                      >
                        ✕ Clear selection
                      </div>
                    )}

                    {/* Options */}
                    {filteredOptions?.length === 0 ? (
                      <div className="text-sm text-cwhite3 p-2">
                        {selectOption?.length === 0
                          ? "No roles available"
                          : "No options found"}
                      </div>
                    ) : (
                      <div className="grid gap-1">
                        {filteredOptions?.map((option, index) => (
                          <div
                            key={index}
                            onClick={() => {
                              field.onChange(option.value);
                              setOpen(false);
                            }}
                            className={cn(
                              "p-2 cursor-pointer flex justify-start rounded-sm hover:bg-cblack6 text-sm text-cwhite2",
                              String(field.value) === String(option.value) && "text-cyellow"
                            )}
                          >
                            {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              )}

              {type === "multiselect" && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      disabled={disabled}
                      className={cn(
                        "w-full text-left font-normal px-3 py-1.5 border border-gray-600 rounded-md text-white min-h-[40px]",
                        (!field.value || field.value.length === 0) &&
                          "text-muted-foreground",
                        style
                      )}
                    >
                      {Array.isArray(selectOption) && selectOption.length === 0
                        ? "No items available"
                        : field?.value?.length
                        ? selectOption
                            .filter((opt) => field.value.includes(opt.value))
                            .map((opt) => opt.label)
                            .join(", ")
                        : placeholder || "Select options"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="min-w-[--radix-popover-trigger-width] bg-cblack7 p-2 max-h-[300px] overflow-y-auto border-gray-600"
                    align="start"
                  >
                    <div className="mb-2">
                      <Input
                        placeholder="Search..."
                        className="bg-cblack6/40 text-cwhite border border-gray-600"
                        onChange={(e) =>
                          setSearchTerm(e.target.value.toLowerCase())
                        }
                      />
                    </div>

                    {filteredOptions.length === 0 ? (
                      <div className="text-sm text-cwhite3 p-2">
                        {selectOption?.length > 0
                          ? "No options match your search"
                          : "No items available"}
                      </div>
                    ) : (
                      <div className="grid gap-1 sm:grid-cols-1 md:grid-cols-2">
                        {filteredOptions.map((option) => {
                          const isChecked = field.value?.includes(option.value);
                          return (
                            <div
                              key={option?.value}
                              onClick={() => {
                                const newValue = isChecked
                                  ? field.value.filter(
                                      (val) => val !== option.value
                                    )
                                  : [...(field.value || []), option.value];
                                field.onChange(newValue);
                              }}
                              className="flex items-center cursor-pointer p-1.5 hover:bg-cblack6 rounded-sm gap-2"
                            >
                              <Checkbox
                                checked={isChecked}
                                onCheckedChange={() => {
                                  const newValue = isChecked
                                    ? field.value.filter(
                                        (val) => val !== option.value
                                      )
                                    : [...(field.value || []), option.value];
                                  field.onChange(newValue);
                                }}
                              />
                              <span className="text-sm text-cwhite2">
                                {option.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              )}

              {type === "date" && (
                <div className="relative text-cwhite2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        className={cn(
                          "w-full text-left font-normal px-3 py-2 border border-gray-600 rounded-md",
                          !field.value && "text-muted-foreground",
                          style
                        )}
                      >
                        {field.value
                          ? format(new Date(field.value), "PPP")
                          : placeholder}

                        <CalendarIcon className="absolute top-2 right-2 inline-block text-gray-400" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0 text-cwhite2 border-gray-600"
                      align="start"
                    >
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={(selectedDate) => {
                          if (selectedDate) {
                            const formattedDate = dateFormat
                              ? format(selectedDate, dateFormat)
                              : selectedDate;
                            setDate(selectedDate);
                            field.onChange(formattedDate);
                          }
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {type === "time" && (
                <Input
                  type="time"
                  placeholder={placeholder}
                  step="60"
                  className={`${style} h-10`}
                  value={field.value ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    const formattedTime =
                      timeFormat === "HH:mm" ? value.slice(0, 5) : value;
                    field.onChange(formattedTime);
                  }}
                  style={{ colorScheme: "dark" }}
                />
              )}

              {type === "textarea" && (
                <Textarea
                  placeholder={placeholder}
                  className={style}
                  // textAreaValue={textAreaValue}
                  {...field}
                />
              )}

              {type === "number" && (
                <Input
                  disabled={disabled}
                  type="number"
                  min={0}
                  step="any"
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "e") e.preventDefault();
                  }}
                  placeholder={placeholder}
                  className={style}
                  // value={field.value ?? ""} 
                  // onChange={(e) => field.onChange(Number(e.target.value))}

                  // value={field.value === undefined || field.value === null ? "" : field.value}
                  // onChange={(e) => {
                  //   const val = e.target.value;
                  //   field.onChange(val === "" ? undefined : Number(val));
                  // }}
                  
                  value={field.value ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    field.onChange(val);
                  }}
                />
              )}


              {type === "checkbox" && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={name}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                  <Label className="cursor-pointer" htmlFor={name}>
                    {label}
                  </Label>
                </div>
              )}

              {type === "radio" && (
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex gap-4"
                >
                  {radioOption?.map((option) => (
                    <div
                      key={option.value}
                      className="flex items-center space-x-2"
                    >
                      <RadioGroupItem value={option.value} id={option.value} />
                      <Label htmlFor={option.value}>{option.label}</Label>
                    </div>
                  ))}
                </RadioGroup>
              )}
            </>
          </FormControl>
          <FormMessage
            className="absolute -bottom-5 truncate max-w-full cursor-pointer text-cred"
            title={form.formState.errors[name]?.message}
          />
        </FormItem>
      )}
    />
  );
}

export default FormInputField;
